/**
 * ThemeToggle
 * Light/dark pill toggle. Persists to localStorage and sets
 * `data-theme` on <html> so the CSS tokens in index.css switch instantly.
 */
import { useEffect, useState } from 'react';

type Theme = 'light' | 'dark';

function readInitial(): Theme {
  if (typeof document === 'undefined') return 'light';
  const attr = document.documentElement.getAttribute('data-theme');
  if (attr === 'dark' || attr === 'light') return attr;
  try {
    const saved = localStorage.getItem('theme');
    if (saved === 'dark' || saved === 'light') return saved;
  } catch { /* ignore */ }
  if (typeof window !== 'undefined' && window.matchMedia?.('(prefers-color-scheme: dark)').matches) {
    return 'dark';
  }
  return 'light';
}

export function ThemeToggle() {
  const [theme, setTheme] = useState<Theme>(readInitial);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    try { localStorage.setItem('theme', theme); } catch { /* ignore */ }
  }, [theme]);

  const isDark = theme === 'dark';

  return (
    <button
      type="button"
      onClick={() => setTheme(isDark ? 'light' : 'dark')}
      aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="relative inline-flex items-center gap-1.5 rounded-full border border-[var(--border-subtle)] bg-[var(--surface-raised)] px-1 py-1 text-xs font-medium text-[var(--text-secondary)] hover:bg-[var(--surface-overlay)] transition-colors"
    >
      <span
        className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
          !isDark ? 'bg-[var(--surface-base)] text-[var(--accent-primary)] shadow-sm' : 'text-[var(--text-muted)]'
        }`}
        aria-hidden="true"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="4" />
          <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
        </svg>
      </span>
      <span
        className={`flex items-center justify-center w-6 h-6 rounded-full transition-colors ${
          isDark ? 'bg-[var(--surface-base)] text-[var(--accent-primary)] shadow-sm' : 'text-[var(--text-muted)]'
        }`}
        aria-hidden="true"
      >
        <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
        </svg>
      </span>
    </button>
  );
}

export default ThemeToggle;
